/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import {
  BuilderPermissionSet,
  BulkActionEntityType,
  UsageRestService,
} from '@gh/builder-shared-data';
import { getRouteInitData, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { UomListBusinessService } from '../../resources/uom-list-business.service';
import { UomListItem } from '../../resources/uom-list-item.model';
import { UomDialogConfig } from '../uom-details/uom-details-dialog.component';
import { uomDialog } from '../uom-details/uom-details-dialog.route';
import { UomGridDefinition } from './uom-grid-definition.service';
import { newGridPaginationCursor } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-uom-list-tab',
  templateUrl: 'uom-list-tab.component.html',
  styleUrls: ['uom-list-tab.component.scss'],
  viewProviders: [UomGridDefinition],
})
export class UomListTabComponent implements OnInit {
  dataSource: SlicedDataSource<UomListItem>;
  loadedData: UomListItem[] = [];
  checkList: any[] = [];
  cursor = newGridPaginationCursor();
  get isShowDeleteSelected(): boolean {
    return !!this.checkList.length;
  }

  constructor(public gridDefinition: UomGridDefinition,
              private userContext: UserContext,
              private route: ActivatedRoute,
              private businessService: UomListBusinessService,
              private navigationService: BuilderNavigationService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private waitingService: WaitingService,
              private notifications: NotificationService,
              private usageService: UsageRestService,
              private dialogService: DialogService) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);

    this.load();

  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'UOM',
      label: label ? label : 'lbl_uom',
    });
  }

  onEdit(uomItem: UomListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.Estimating.UoM.EDIT_VIEW)) {
      this.dialogService.openWithInput(uomDialog, <UomDialogConfig>{
          id: uomItem.id,
        })
        .afterClosed()
        .subscribe((uomDetails) => {
          if (uomDetails) {
            this.load();
          }
        });
    }
  }

  onCreate(): void {
    this.dialogService.openWithInput(uomDialog, {})
      .afterClosed()
      .subscribe((uomDetails) => {
        if (uomDetails) {
          this.load();
        }
      });
  }

  onDelete(uomItem: UomListItem): void {
    const flow = this.pipelines.toggleItem({
      question: { id: 'msg_uom_delete', params: { code: uomItem.code } },
      validate: this.commonActions.validate((state: CommonFlowState<UomListItem>) =>
        this.businessService.checkUsage(state.value)),
      execute: this.commonActions.execute((state: CommonFlowState<UomListItem>) =>
        this.businessService.remove(state.value)),
      finish: () => this.load(),
    });

    flow.run({
      value: uomItem,
    });
  }

  load(): void {
    this.dataSource = this.businessService.findAllAsDataSource();
  }

  onDeleteSelected(): void {
    const entityList = this.loadedData
      .filter((model) => this.checkList.includes(model.id))
      ?.map((item) => ({id: item.id, version: item.version}));

    this.usageService.getBulkUsageInfo(<any[]>entityList, BulkActionEntityType.uom)
      .pipe(this.waitingService.operator())
      .subscribe(() => {
        this.dialogService.confirmation(
          yesNoConfirmation('msg_uoms_delete'),
        ).pipe(
          filter$(yesAnswer),
        ).subscribe(
          () => this.businessService
            .bulkDelete(this.loadedData
                          .filter((model) => this.checkList.includes(model.id))
                          ?.map((item) => ({id: item.id, usage: {deleted: item.canBeDeleted, version: item.version }})))
            .subscribe(() => this.load(), this.notifications.processAll),
          this.notifications.processAll);
      },
                 this.notifications.processAll);
  }

  isActionButtonsVisible(): boolean {
    return !this.checkList.length;
  }

  onLoadData(event: any): void {
    this.checkList = [];
    this.loadedData = event.data;
  }

  rowClicked(event: any): void {
    if (event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.event.shiftKey) {
      this.onCheckChange(event.data.id);
      return;
    }
    if (this.isActionButtonsVisible()) {
      this.onEdit(event.data);
    }
  }

  onToggleSelectAll(): void {
    if (!this.checkList.length || (!!this.checkList.length && this.checkList.length < this.loadedData.length)) {
      this.checkList = this.loadedData?.map((item) => item.id);
      return;
    }
    this.checkList = [];
  }

  onCheckChange(id: ID): void {
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      return;
    }
    this.checkList = [...this.checkList, id];
  }

}
